/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Img, Box, useTheme} from '@chakra-ui/react'
import {getResponsiveImageAttributes} from '../../utils/responsive-image'
import {Helmet} from 'react-helmet'
import {useNonce} from '../../contexts'

// DO NOT USE MODERN JS INSIDE OR REFERENCE ANYTHING OUTSIDE OF THIS FUNCTION
const preloadImageFunction = (props) => {
    const image = new Image()
    image.src = props.src
    image.srcset = props.srcSet
    image.sizes = props.sizes
    image.loading = 'eager'
}

/**
 * Quickly create a responsive image using your dynamic image service
 * @example
 *  // Widths without a unit are interpreted as px values
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={[100, 360, 720]} />
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={{base: 100, sm: 360, md: 720}} />
 * // You can also use units of px or vw
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={['50vw', '100vw', '500px']} />
 */
const DynamicImage = ({src, widths, imageProps, as, enableZoom, preload, ...rest}) => {
    const Component = as ? as : Img
    const theme = useTheme()
    const nonce = useNonce()
    const [state, setState] = useState({
        transformOriginTop: 0,
        transformOriginLeft: 0,
        isZoomed: false,
        translateX: 0,
        translateY: 0
    })

    // The state is true if the image should be preloaded, but once client is hydrated, we set it to false as no more preloading is needed
    const [shouldPreload, setShouldPreload] = useState(preload)
    useEffect(() => {
        setShouldPreload(false)
    }, [])

    const scaleFactor = 2

    const responsiveImageProps = useMemo(
        () => getResponsiveImageAttributes({src, widths, breakpoints: theme.breakpoints}),
        [src, widths, theme.breakpoints]
    )

    const imageContainerRef = useRef(null)

    // Returns the mouse X and Y relative to the image container
    const getMouseXY = (ev) => {
        const rect = imageContainerRef.current.getBoundingClientRect()
        let x = parseInt(ev.clientX - rect.left)
        x = x < 0 ? 0 : x
        let y = parseInt(ev.clientY - rect.top)
        y = y < 0 ? 0 : y

        return {x, y}
    }

    // Calculates the transform origin top and left positions
    const calculateTransformOriginPositions = (zoomed, ev) => {
        let top = 0
        let left = 0

        if (zoomed && imageContainerRef && imageContainerRef.current) {
            const {x, y} = getMouseXY(ev)
            top = !state.isZoomed ? y : 0 - y
            left = !state.isZoomed ? x : 0 - x

            top = y
            left = x
        }

        return {
            transformOriginTop: top,
            transformOriginLeft: left
        }
    }

    const calculateTranslatePositions = (ev) => {
        const {x, y} = getMouseXY(ev)
        const left = x - state.transformOriginLeft
        const top = y - state.transformOriginTop

        return {
            translateX: left / 2,
            translateY: top / 2
        }
    }

    const toggleIsZoomed = (ev) => {
        setState({
            isZoomed: !state.isZoomed,
            translateX: 0,
            translateY: 0,
            ...calculateTransformOriginPositions(!state.isZoomed, ev)
        })
    }

    const mouseMoveHandler = (ev) => {
        if (!state.isZoomed) {
            return
        }

        setState({
            ...state,
            ...calculateTranslatePositions(ev)
        })
    }

    const getStyles = () => {
        return {
            transform: state.isZoomed
                ? `scale(${scaleFactor}) translateX(${state.translateX}px) translateY(${state.translateY}px)`
                : null,
            transformOrigin: state.isZoomed
                ? `${state.transformOriginLeft}px ${state.transformOriginTop}px`
                : null
        }
    }

    return (
        <Box
            {...rest}
            ref={imageContainerRef}
            onMouseMove={enableZoom ? mouseMoveHandler : null}
            onClick={enableZoom ? toggleIsZoomed : null}
            overflow="hidden"
            height="100%"
        >
            {shouldPreload && (
                <Helmet>
                    {/* inline script to preload the image as soon as header is received by the browser */}
                    <script lang="javascript" type="text/javascript" nonce={nonce}>
                        {`(${
                            // inline the function
                            preloadImageFunction.toString()
                        })(${
                            // pass the args as json
                            JSON.stringify(responsiveImageProps)
                        })`}
                    </script>
                </Helmet>
            )}
            <Component
                {...responsiveImageProps}
                {...imageProps}
                {...(enableZoom ? getStyles() : null)}
                cursor={state.isZoomed ? 'grab' : enableZoom ? 'zoom-in' : 'default'}
                data-testid="dynamic-image"
                height="100%"
            />
        </Box>
    )
}

DynamicImage.propTypes = {
    /**
     * Dynamic src having an optional param that can vary with widths. For example: `image[_{width}].jpg` or `image.jpg[?sw={width}&q=60]`
     */
    src: PropTypes.string,
    /**
     * Image widths relative to the breakpoints, whose units can either be px or vw or unit-less. They will be mapped to the corresponding `sizes` and `srcSet`.
     */
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    /**
     * Props to pass to the inner image component
     */
    imageProps: PropTypes.object,
    /**
     * Override with your chosen image component
     */
    as: PropTypes.elementType,
    /**
     * Enables the zoom functionality
     */
    enableZoom: PropTypes.bool,
    /**
     * Should the image be preloaded
     */
    preload: PropTypes.bool
}

export default DynamicImage
